/*
 * ******************************************************************
 * Copyright (c) 2024-2026 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild } from "@angular/core";
import { ClrDatagridFilter, ClrDatagridFilterInterface, ClrPopoverService } from "@clr/angular";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    standalone: false,
  selector: 'dg-custom-string-filter',
  templateUrl: './datagrid-custom-string-filter.component.html',
  styleUrls: ['./datagrid-custom-string-filter.component.scss']
})
export class DatagridCustomStringFilter<T> implements ClrDatagridFilterInterface<T>, AfterViewInit, OnDestroy {
    @ViewChild('input', { read: ElementRef })
    private inputRef: ElementRef;

    @Input()
    public filterProperty: string;

    @Input()
    public pattern: string;

    public _value: string;
    set value(v: string) {
        const prevValue = this._value;
        this._value = v;
        if (this._value &&
            ((this.pattern && this._value.match(this.pattern)) || (!this.pattern && this._value.length > 0))
            || prevValue) {
            this.valid = true;
        } else {
            this.valid = false;
        }
    }
    get value(): string {
        return this._value;
    }

    /**
     * Preset filter selection.
     * @param value - array of strings
     */
    @Input()
    public set clrFilterValue(value: string) {
        this.value = value;
        this.applied = this.valid;
    }
    public get clrFilterValue(): string {
        return this.value;
    }

    @Output()
    clrFilterValueChange: EventEmitter<string> = new EventEmitter();

    public valid: boolean = false;
    public applied: boolean = false;

    private changesSubject: Subject<T> = new Subject<T>();
    private destroySubject: Subject<void> = new Subject<void>();

    constructor(protected filterContainer: ClrDatagridFilter,
                private smartToggleService: ClrPopoverService,
                private ngZone: NgZone) {
        filterContainer.setFilter(this);
    }

    public get changes(): Observable<T> {
        return this.changesSubject.asObservable();
    }

    get state(): any {
        return {
            property: this.filterProperty,
            value: [this.value]
        }
    }

    accepts(_item: T): boolean {
        // This is not used for server driven filtering
        return false;
    }

    isActive(): boolean {
        return this.applied;
    }

    ngAfterViewInit(): void {
        this.smartToggleService.openChange
            .pipe(takeUntil(this.destroySubject))
            .subscribe(() => {
                this.ngZone.runOutsideAngular(() => {
                    requestAnimationFrame(() => {
                        this.inputRef.nativeElement.focus();
                    });
                });
        });
    }

    onButtonClick(): void {
        if (!this.value) {
            this.onClearClick();
            return;
        }
        this.applied = true;

        this.clrFilterValueChange.emit(this.value);
        this.changesSubject.next(null);
        this.smartToggleService.open = false;
    }

    onClearClick(): void {
        this.value = null;
        this.applied = false;
        this.valid = false;

        this.clrFilterValueChange.emit(this.value);
        this.changesSubject.next(null);
    }

    ngOnDestroy(): void {
        this.destroySubject.next();
        this.destroySubject.complete();
    }

}
